import React from 'react';
import { useTranslation } from 'react-i18next';
import { ArrowLeft, Home, Info, Phone, Mail, MapPin } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { useNavigate } from 'react-router-dom';
import SimulateurHabitatOutremer from '../components/SimulateurHabitatOutremer';

export default function SimulateurHabitatPage() {
  const { t: t } = useTranslation();
  const navigate = useNavigate();
  
  const handleSimulationComplete = (_result) => {
    // Ici on pourrait sauvegarder ou rediriger
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header de navigation */}
      <div className="bg-white/80 backdrop-blur-sm border-b sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 py-4 flex items-center justify-between">
          <Button 
            variant="ghost" 
            onClick={() => navigate('/')}
            className="flex items-center gap-2"
          >
            <ArrowLeft className="h-4 w-4" />
            Retour à l'accueil
          </Button>
          
          <div className="flex items-center gap-2">
            <Home className="h-5 w-5 text-indigo-600" />
            <span className="font-semibold text-gray-900">Simulateur Habitat O'SCOP</span>
          </div>
        </div>
      </div>
      
      {/* Contenu principal */}
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Simulateur - Colonne principale */}
          <div className="lg:col-span-2">
            <SimulateurHabitatOutremer 
              onSimulationComplete={handleSimulationComplete}
              showHeader={true}
            />
          </div>
          
          {/* Sidebar - Informations */}
          <div className="space-y-6">
            {/* Card Avantages */}
            <Card className="border-0 shadow-lg">
              <CardHeader className="bg-gradient-to-r from-green-600 to-emerald-600 text-white rounded-t-lg">
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="h-5 w-5" />
                  Pourquoi choisir O'SCOP ?
                </CardTitle>
              </CardHeader>
              <CardContent className="p-4 space-y-4">
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-green-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">1</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Économie Sociale et Solidaire</h4>
                    <p className="text-sm text-gray-600">Un dispositif coopératif au service des territoires ultramarins</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-blue-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">2</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Aides régionales</h4>
                    <p className="text-sm text-gray-600">Bénéficiez des dispositifs d'aide adaptés à votre territoire</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-purple-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">3</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Dotation FOGEDOM</h4>
                    <p className="text-sm text-gray-600">Une garantie solidaire pour sécuriser votre projet</p>
                  </div>
                </div>
                
                <div className="flex items-start gap-3">
                  <div className="w-8 h-8 rounded-full bg-amber-600 flex items-center justify-center flex-shrink-0">
                    <span className="text-white font-bold">4</span>
                  </div>
                  <div>
                    <h4 className="font-semibold text-gray-900">Accompagnement personnalisé</h4>
                    <p className="text-sm text-gray-600">Nos conseillers vous accompagnent à chaque étape</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Card Contact */}
            <Card className="border-0 shadow-lg">
              <CardHeader>
                <CardTitle className="text-lg">Besoin d'aide ?</CardTitle>
              </CardHeader>
              <CardContent className="space-y-3">
                <a href="tel:+590590000000" className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Phone className="h-5 w-5 text-green-600" />
                  <div>
                    <p className="font-medium text-gray-900">0590 00 00 00</p>
                    <p className="text-xs text-gray-500">Du lundi au vendredi, 8h-17h</p>
                  </div>
                </a>
                
                <a href="mailto:contact@objectifscopoutremer.com" className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 transition-colors">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <p className="font-medium text-gray-900">contact@objectifscopoutremer.com</p>
                    <p className="text-xs text-gray-500">Réponse sous 48h</p>
                  </div>
                </a>
                
                <div className="flex items-center gap-3 p-3 rounded-lg bg-gray-50">
                  <MapPin className="h-5 w-5 text-red-600" />
                  <div>
                    <p className="font-medium text-gray-900">Nos agences</p>
                    <p className="text-xs text-gray-500">Guadeloupe, Martinique, Guyane, Réunion, Mayotte</p>
                  </div>
                </div>
              </CardContent>
            </Card>
            
            {/* Disclaimer légal */}
            <Card className="border-amber-200 bg-amber-50">
              <CardContent className="p-4">
                <p className="text-xs text-amber-800">
                  <strong>Mention légale :</strong> Ce simulateur fournit une estimation indicative basée sur les informations saisies. 
                  Les résultats ne constituent en aucun cas une offre de financement ni un engagement contractuel. 
                  Seule une étude personnalisée par nos conseillers permettra de déterminer votre éligibilité définitive.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}
